import { endpoints } from '@/api/endpoints/endpoints';
import ActionPopover from '@/components/action/action-popover';
import AsyncDataTable from '@/components/core/async-data-table/async-data-table';
import FilterHeader from '@/components/core/async-data-table/filter-header';
import { DatableColumnType } from '@/components/core/async-data-table/type';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { routes } from '@/routes/route-constants';
import { useDeleteCustomer } from '@/services/customer-service';
import { Phone, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';

// Responsive Hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 768);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);
  return isMobile;
};

const CustomerAsyncTable = () => {
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();
  const deleteCustomer = useDeleteCustomer();
  const [refreshPage, setRefreshPage] = useState(false);
  const { toast } = useToast();

  const handleDelete = async (customerId: string) => {
    try {
      await deleteCustomer.executeAsync(customerId);
      toast({ title: 'Success', description: 'Customer deleted successfully' });
      setRefreshPage(!refreshPage);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete customer', variant: 'destructive' });
    }
  };

  const cols: DatableColumnType[] = useMemo(
    () => [
      {
        name: 'name',
        id: 'name',
        label: 'Customer',
        searchable: true,
        orderable: true,
        datatype: 'string' as const,
        Render: ({ row }: { row: any }) => {
          const name = row?.name || 'Unknown';
          return (
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9 border shadow-sm">
                <AvatarImage src={row.profileImageUrl} alt={row.name} />
                <AvatarFallback className="bg-indigo-100 text-indigo-700 text-[10px] font-bold">
                  {name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 truncate">{row.name}</h4>
                {isMobile && row.saleChannel && (
                  <span className="text-[10px] text-muted-foreground uppercase tracking-tight">
                    {row.saleChannel}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      // Hide standalone phone on mobile, merge into Spent column or name
      ...(!isMobile
        ? [
            {
              name: 'phone',
              id: 'phone',
              label: 'Phone',
              searchable: true,
              datatype: 'string' as const,
            },
            {
              name: 'address',
              id: 'address',
              label: 'Address',
              datatype: 'string' as const,
            },
            {
              name: 'saleChannel',
              id: 'saleChannel',
              label: 'Channel',
              searchable: true,
              datatype: 'string' as const,
            },
          ]
        : []),
      {
        name: 'totalSpend',
        id: 'total_spend',
        label: isMobile ? 'Overview' : 'Spent',
        orderable: true,
        datatype: 'custom' as const,
        Render: ({ row }: { row: any }) => {
          const amount = row.totalSpend ?? 0;
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(amount);

          return (
            <div className={`${isMobile ? 'text-right' : 'text-left'} flex flex-col`}>
              <span className="text-sm font-bold text-green-700">{formatted}</span>
              {isMobile && row.phone && (
                <span className="text-[10px] text-gray-500 flex items-center justify-end gap-1">
                  <Phone size={10} /> {row.phone}
                </span>
              )}
            </div>
          );
        },
      },
      // Hide Creation Date on mobile to prioritize financial info
      ...(!isMobile
        ? [
            {
              name: 'createdAt',
              id: 'createdAt',
              label: 'Joined',
              Render: ({ row }: { row: any }) => {
                if (!row.createdAt) return '-';
                return (
                  <span className="text-xs">{new Date(row.createdAt).toLocaleDateString()}</span>
                );
              },
            },
          ]
        : []),
      {
        name: 'action',
        id: 'action',
        label: '',
        Render: ({ row }: { row: any }) => (
          <ActionPopover
            onEdit={() => setLocation(routes.customer.edit(row.id))}
            onDelete={() => handleDelete(row.id)}
          />
        ),
      },
    ],
    [isMobile, setLocation],
  );

  return (
    <div className="customer-table-mobile-wrapper">
      <AsyncDataTable
        dataUrl={endpoints.customer.list}
        columns={cols}
        autoIndex={!isMobile} // Hide index on mobile to save space
        FilterComponent={FilterHeader}
        refreshPage={refreshPage}
        showPageHeader={false}
        orderByDef="updated_at"
        headerAction={() => setLocation('/customers/new')}
        onClickRow={(row) => setLocation(routes.customer.detail(row.id))}
        headerActionText="Add Customer"
        headerActionIcon={<Plus size={18} />}
      />
    </div>
  );
};

export default CustomerAsyncTable;
